import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { requestV3 } from 'Features/Auth/axios-client'

export type OptimiserTableResponse = {
  data: Record<string, unknown>[]
  columns: string[]
  total?: number
  page?: number
  pageSize?: number
}

export type RunConfig = {
  phase: number
  service_mode: 'lost_sales' | 'backorder'
  moq_enforcement: 'hard' | 'soft' | 'soft_indicator' | 'hard_rounded'
  time_limit: number
  mip_gap: number
}

export type CostBreakdown = {
  landed: number
  holding: number
  service: number
  bands: number
  minutil: number
  commit: number
  moq: number
  terminal_value: number
  total: number
}

export type RunKPIs = {
  service_level: number
  fill_rate: number
  total_holding_cost: number
  expediting_cost: number
  bottleneck_utilisation: number
  expiry_units: number
  cost_breakdown: CostBreakdown
}

export type RunResult = {
  run_id: string
  scenario_id: string | null
  status: string
  termination_condition: string
  objective: number | null
  runtime_s: number | null
  n_vars: number
  n_cons: number
  cost_breakdown: CostBreakdown
  kpis: RunKPIs | null
  phase: number
  service_mode: string
  moq_enforcement: string
  time_limit: number
  mip_gap: number
  outputs: string[]
}

export type OptimiserScenario = {
  scenario_id: string
  name: string
  created_at: string
  created_by: string
  tables_copied: number
}

export type ScenariosResponse = {
  scenarios: OptimiserScenario[]
  total: number
}

export const useOptimiserScenarios = () =>
  useQuery<ScenariosResponse>({
    queryKey: ['optimiser-scenarios'],
    queryFn: () =>
      requestV3({ url: '/optimiser/scenarios', method: 'GET' }).then((r) => r.data),
    staleTime: 30 * 1000,
  })

export const useCreateOptimiserScenario = () => {
  const queryClient = useQueryClient()
  return useMutation<OptimiserScenario, Error, { name: string }>({
    mutationFn: (data) =>
      requestV3({ url: '/optimiser/scenarios', method: 'POST', data }).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['optimiser-scenarios'] })
    },
  })
}

export const useRunSolver = (scenarioId?: string) =>
  useMutation<RunResult, Error, RunConfig>({
    mutationFn: (cfg) =>
      requestV3({
        url: '/optimiser/run',
        method: 'POST',
        data: { ...cfg, ...(scenarioId ? { scenario_id: scenarioId } : {}) },
      }).then((r) => r.data),
  })

export type RunsResponse = {
  runs: RunResult[]
  total: number
}

export const useOptimiserRuns = (scenarioId?: string) =>
  useQuery<RunsResponse>({
    queryKey: ['optimiser-runs', scenarioId ?? 'base'],
    queryFn: () =>
      requestV3({
        url: '/optimiser/runs',
        method: 'GET',
        params: scenarioId ? { scenario_id: scenarioId } : undefined,
      }).then((r) => r.data),
    staleTime: 30 * 1000,
  })

export const useOptimiserRunOutput = (
  runId: string,
  tableName: string,
  page: number,
  pageSize: number,
  scenarioId?: string,
) =>
  useQuery<OptimiserTableResponse>({
    queryKey: ['optimiser-run-output', runId, tableName, page, pageSize, scenarioId ?? 'base'],
    queryFn: () =>
      requestV3({
        url: `/optimiser/runs/${runId}/outputs/${tableName}`,
        method: 'GET',
        params: {
          page,
          pageSize,
          ...(scenarioId ? { scenario_id: scenarioId } : {}),
        },
      }).then((r) => r.data),
    enabled: !!runId && !!tableName,
    staleTime: 5 * 60 * 1000,
  })

export const useUpdateOptimiserView = (viewName: string, scenarioId?: string) => {
  const queryClient = useQueryClient()
  return useMutation<{ updated_tables: string[] }, Error, Record<string, unknown>[]>({
    mutationFn: (rows) =>
      requestV3({
        url: `/optimiser/views/${viewName}`,
        method: 'PUT',
        params: scenarioId ? { scenario_id: scenarioId } : undefined,
        data: rows,
      }).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['optimiser-view', viewName],
      })
    },
  })
}

export const useUpdateOptimiserInputTable = (tableName: string, scenarioId?: string) => {
  const queryClient = useQueryClient()
  return useMutation<{ saved: number }, Error, Record<string, unknown>[]>({
    mutationFn: (rows) =>
      requestV3({
        url: `/optimiser/inputs/${tableName}`,
        method: 'PUT',
        params: scenarioId ? { scenario_id: scenarioId } : undefined,
        data: rows,
      }).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['optimiser-input', tableName, scenarioId ?? 'base'],
      })
    },
  })
}

export const useOptimiserInputTable = (tableName: string, scenarioId?: string) =>
  useQuery<OptimiserTableResponse>({
    queryKey: ['optimiser-input', tableName, scenarioId ?? 'base'],
    queryFn: () =>
      requestV3({
        url: `/optimiser/inputs/${tableName}`,
        method: 'GET',
        params: scenarioId ? { scenario_id: scenarioId } : undefined,
      }).then((r) => r.data),
    enabled: !!tableName,
    staleTime: 5 * 60 * 1000,
  })

export const useRunOutputFull = (runId: string, tableName: string, scenarioId?: string) =>
  useQuery<OptimiserTableResponse>({
    queryKey: ['optimiser-chart-full', runId, tableName, scenarioId ?? 'base'],
    queryFn: () =>
      requestV3({
        url: `/optimiser/runs/${runId}/outputs/${tableName}`,
        method: 'GET',
        params: {
          page: 0,
          pageSize: 10000,
          ...(scenarioId ? { scenario_id: scenarioId } : {}),
        },
      }).then((r) => r.data),
    enabled: !!runId && !!tableName,
    staleTime: 10 * 60 * 1000,
  })

export const useOptimiserView = (
  viewName: string,
  page: number,
  pageSize: number,
  scenarioId?: string,
) =>
  useQuery<OptimiserTableResponse>({
    queryKey: ['optimiser-view', viewName, page, pageSize, scenarioId ?? 'base'],
    queryFn: () =>
      requestV3({
        url: `/optimiser/views/${viewName}`,
        method: 'GET',
        params: {
          page,
          pageSize,
          ...(scenarioId ? { scenario_id: scenarioId } : {}),
        },
      }).then((r) => r.data),
    enabled: !!viewName,
    staleTime: 5 * 60 * 1000,
  })
